import { AgentDisposedError } from "../../../errors.js";
import type { AgentOptions, ModelSelection } from "../../../types/agent.js";
import type { Run, SDKUserMessage, SendOptions } from "../../../types/run.js";
import { emitRunEvent } from "../../../types/run-events.js";
import type { MemoryToolSpec } from "../../agent-loop/loop-types.js";
import type { PluginManager } from "../../plugins/manager.js";
import type { TelemetryHandle } from "../../telemetry/tracer.js";
import { anySignal } from "../concurrency/abort-utils.js";
import {
  type CompletionCheckDeps,
  wrapRunWithCompletionCheck,
} from "../lifecycle/wrap-completion-check-run.js";
import {
  resolveActiveMemorySummaryForSend,
  resolveMemoryProviderForLoop,
  resolveMemoryToolsForLoop,
  shouldUsePortMemoryPath,
} from "../memory/memory-path-selector.js";
import type { MemoryProvider } from "../memory/memory-provider.js";
import type { MemoryFact } from "../memory/memory-store.js";
import { readMemoryFacts } from "../memory/memory-store.js";
import { normalizeModel } from "../model-selection.js";
import { runInputProcessors } from "../processors/run-processors.js";
import { createTripwireRun } from "../processors/tripwire-run.js";
import { wrapRunWithOutputProcessors } from "../processors/wrap-output-run.js";
import { appendSessionMessage, getSessionMessages } from "../session/agent-session.js";
import { safeCall } from "../system-prompt/safe-call.js";
import { consumePending } from "./local-agent-invalidate.js";
import type { LocalAgentMemory } from "./local-agent-memory.js";
import { applyPreUserSendHook, wrapRunWithPostReplyHook } from "./local-agent-memory-hooks.js";
import { persistMemoryFactIfWritePrompt } from "./local-agent-runtime-extensions.js";

/**
 * Inputs for the `executeSendLocked` helper extracted from LocalAgent.sendLocked.
 * @internal
 */
export interface SendLockedInputs {
  agentId: string;
  disposed: boolean;
  invalidationPending: { reason: string; at: number } | undefined;
  clearInvalidation: () => void;
  reload: () => Promise<void>;
  // SE8 — receives an already-NORMALIZED ModelSelection (the string shorthand is
  // normalized at the send boundary), decoupled from the public AgentOptions type.
  applyModelOverride: (model: ModelSelection | undefined) => void;
  options: AgentOptions;
  pluginManagerCode: PluginManager;
  memoryGlue: LocalAgentMemory;
  defaultMemoryProviderForLoop: ReturnType<
    typeof import("./local-agent-memory-provider.js").createLocalAgentMemoryProvider
  >;
  workspaceCwd: string;
  telemetry: TelemetryHandle;
  lifecycleAbortController: AbortController;
  runPreHook: (userText: string) => Promise<void>;
  resolveSystemPromptForSend: (
    userText: string,
    options: SendOptions,
    memoryFacts: ReadonlyArray<MemoryFact>,
  ) => Promise<string | undefined>;
  assembleSystemPromptForSend: (
    userText: string,
    baseSystemPrompt: string | undefined,
    memoryFacts: ReadonlyArray<MemoryFact>,
    activeMemorySummary: string | undefined,
    contextPaths: readonly string[] | undefined,
  ) => Promise<string | undefined>;
  // jscpd:ignore-start — type contract mirrors LocalAgent.dispatchRun signature (not knowledge duplication)
  dispatchRun: (
    message: string | SDKUserMessage,
    options: SendOptions,
    systemPrompt: string | undefined,
    memoryFacts: ReadonlyArray<MemoryFact>,
    priorMessages: ReadonlyArray<{ role: "user" | "assistant"; text: string }>,
    memoryTools: ReadonlyArray<MemoryToolSpec> | undefined,
    memoryProviderOverride?: MemoryProvider,
  ) => Promise<Run>;
  // jscpd:ignore-end
}

/**
 * SE24 — run the input guardrail processors on the raw user text. Returns a
 * terminal `tripwireRun` when a processor blocked, else the (possibly rewritten)
 * `userText` + `effectiveMessage` to feed the rest of the send.
 */
async function applyInputProcessors(
  inputs: SendLockedInputs,
  message: string | SDKUserMessage,
  rawUserText: string,
  options: SendOptions,
  sendModel: ModelSelection | undefined,
): Promise<{ tripwireRun: Run } | { userText: string; effectiveMessage: string | SDKUserMessage }> {
  const processors = inputs.options.inputProcessors;
  if (processors === undefined || processors.length === 0) {
    return { userText: rawUserText, effectiveMessage: message };
  }
  const res = await runInputProcessors(processors, rawUserText, inputs.agentId);
  if (res.kind === "tripwire") {
    emitRunEvent(options.onRunEvent, {
      type: "tripwire",
      reason: res.tripwire.reason,
      processorId: res.tripwire.processorId,
    });
    return {
      tripwireRun: createTripwireRun({
        agentId: inputs.agentId,
        tripwire: res.tripwire,
        model: sendModel,
      }),
    };
  }
  const effectiveMessage =
    typeof message === "string" ? res.value : { ...message, text: res.value };
  return { userText: res.value, effectiveMessage };
}

/**
 * Core send logic extracted from `LocalAgent.sendLocked` to reduce
 * the LocalAgent class LoC (G8 budget). All dependencies are injected
 * via the `inputs` parameter.
 *
 * @internal
 */
export async function executeSendLocked(
  inputs: SendLockedInputs,
  message: string | SDKUserMessage,
  options: SendOptions,
): Promise<Run> {
  if (inputs.disposed) throw new AgentDisposedError(inputs.agentId);
  // biome-ignore format: keep one-liner to stay under G8 LoC.
  await consumePending(inputs.agentId, inputs.invalidationPending, inputs.clearInvalidation, inputs.reload);
  // SE8 — normalize a bare-string send-model override to `{ id }`.
  const sendModel = normalizeModel(options.model);
  inputs.applyModelOverride(sendModel);
  const rawUserText = typeof message === "string" ? message : message.text;

  // SE24 — input guardrail processors run FIRST, before any side effect; a
  // `block` short-circuits to a tripwire run, a `rewrite` flows downstream.
  const gated = await applyInputProcessors(inputs, message, rawUserText, options, sendModel);
  if ("tripwireRun" in gated) return gated.tripwireRun;
  const { userText, effectiveMessage } = gated;

  if (inputs.options.onBeforeSend !== undefined) {
    await inputs.options.onBeforeSend({
      conversationId: inputs.agentId,
      previousMessageCount: getSessionMessages(inputs.agentId).length,
    });
  }
  await inputs.runPreHook(userText);

  const adaptedMessage = await applyPreUserSendHook({
    pluginManager: inputs.pluginManagerCode,
    agentId: inputs.agentId,
    options: inputs.options,
    original: effectiveMessage,
    userText,
    sendOptions: options,
  });

  const priorMessages = [...getSessionMessages(inputs.agentId)];
  appendSessionMessage(inputs.agentId, { role: "user", text: userText });

  await persistMemoryFactIfWritePrompt(inputs.workspaceCwd, inputs.options.memory, userText);
  const memoryFacts = await readMemoryForSend(inputs.workspaceCwd, inputs.options.memory);
  const portPathActive = shouldUsePortMemoryPath();
  const legacyTools = portPathActive ? undefined : await inputs.memoryGlue.ensureTools();
  const legacySummary = portPathActive
    ? undefined
    : await inputs.memoryGlue.runActiveMemoryIfEnabled(userText, priorMessages, inputs.telemetry);
  const memoryTools = resolveMemoryToolsForLoop(legacyTools, portPathActive);
  const activeMemorySummary = resolveActiveMemorySummaryForSend(legacySummary, portPathActive);
  const effectiveMemoryProvider = resolveMemoryProviderForLoop(
    inputs.options.memoryProvider,
    inputs.defaultMemoryProviderForLoop,
    portPathActive,
  );
  const baseSystemPrompt = await inputs.resolveSystemPromptForSend(userText, options, memoryFacts);
  const assembledSystemPrompt = await inputs.assembleSystemPromptForSend(
    userText,
    baseSystemPrompt,
    memoryFacts,
    activeMemorySummary,
    options.contextPaths,
  );
  const composedOptions: SendOptions = {
    ...options,
    signal: anySignal([options.signal, inputs.lifecycleAbortController.signal]),
  };
  const run = await inputs.dispatchRun(
    adaptedMessage,
    composedOptions,
    assembledSystemPrompt,
    memoryFacts,
    priorMessages,
    memoryTools,
    effectiveMemoryProvider,
  );
  // SE24 — output processors wrap INNER (they redact/block the model text) so the
  // post_assistant_reply memory hook observes the FINAL (processed) reply.
  const outputProcessors = inputs.options.outputProcessors;
  const processedRun =
    outputProcessors !== undefined && outputProcessors.length > 0
      ? wrapRunWithOutputProcessors({
          run,
          processors: outputProcessors,
          agentId: inputs.agentId,
          onRunEvent: options.onRunEvent,
        })
      : run;
  const hookedRun = wrapRunWithPostReplyHook({
    pluginManager: inputs.pluginManagerCode,
    agentId: inputs.agentId,
    options: inputs.options,
    run: processedRun,
    userText,
  });
  // SE34 — outermost wrap: judge the FINAL reply against the per-send completion
  // criterion (opt-in). Absent `completionCheck` ⇒ returns `hookedRun` unchanged.
  return wrapRunWithCompletionCheck({
    run: hookedRun,
    completionCheck: options.completionCheck,
    onRunEvent: options.onRunEvent,
    deps: buildCompletionCheckDeps(),
  });
}

/** Resolve the LLM-judge dep from the DI registry (mirrors runUntil's judge wiring). */
function buildCompletionCheckDeps(): CompletionCheckDeps {
  return {
    judge: async (ctx, opts) => {
      const { judgeCallImpl } = await import("../../judge/judge-call.js");
      const { getAgentFacade } = await import("../registry/agent-factory-registry.js");
      return judgeCallImpl(ctx, opts, { create: getAgentFacade().create });
    },
  };
}

function readMemoryForSend(
  workspaceCwd: string,
  memoryConfig: AgentOptions["memory"],
): Promise<MemoryFact[]> {
  if (memoryConfig?.enabled !== true) return Promise.resolve([]);
  return safeCall(() => readMemoryFacts(workspaceCwd, memoryConfig), [], "memory read");
}
